import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Package, CheckCircle, XCircle, Clock, TrendingUp, Truck } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import FormField, { Input, Select, Textarea, FormRow, SubmitBtn } from '../components/ui/FormField';

const STATUS_COLORS: any = {
  draft: { bg:'var(--border)', color:'var(--text-muted)', label:'დრაფტი' },
  ordered: { bg:'var(--accent-bg)', color:'var(--accent)', label:'შეკვეთილი' },
  received: { bg:'var(--success-bg)', color:'var(--success)', label:'მიღებული' },
  cancelled: { bg:'var(--danger-bg)', color:'var(--danger)', label:'გაუქმებული' },
};

export default function AcquisitionsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'orders'|'budgets'|'suppliers'>('orders');
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [showNewBudget, setShowNewBudget] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderItems, setOrderItems] = useState([{ title:'', author:'', isbn:'', quantity:1, price:0 }]);
  const [orderForm, setOrderForm] = useState({ supplier_id:'', budget_id:'', order_date:'', expected_date:'', notes:'' });
  const [supplierForm, setSupplierForm] = useState({ name:'', contact:'', email:'', phone:'', address:'' });
  const [budgetForm, setBudgetForm] = useState({ name:'', amount:'', year: new Date().getFullYear().toString() });
  const [saving, setSaving] = useState(false);
  const [receiveOrder, setReceiveOrder] = useState<any>(null);
  const [receiveForm, setReceiveForm] = useState({ account_code:'', acquisition_price:'', receive_date: new Date().toISOString().slice(0,10) });

  const { data: ordersData } = useQuery({ queryKey: ['orders'], queryFn: () => api.get('/acquisitions/orders') } as any);
  const { data: suppliersData } = useQuery({ queryKey: ['suppliers'], queryFn: () => api.get('/acquisitions/suppliers') } as any);
  const { data: budgetsData } = useQuery({ queryKey: ['budgets'], queryFn: () => api.get('/acquisitions/budgets') } as any);
  const { data: orderDetail } = useQuery({ queryKey: ['order', selectedOrder?.id], queryFn: () => api.get(`/acquisitions/orders/${selectedOrder?.id}`), enabled: !!selectedOrder?.id } as any);

  const orders = (ordersData as any)?.data?.orders || [];
  const suppliers = (suppliersData as any)?.data?.suppliers || [];
  const budgets = (budgetsData as any)?.data?.budgets || [];
  const detail = (orderDetail as any)?.data;

  const addItem = () => setOrderItems(prev => [...prev, { title:'', author:'', isbn:'', quantity:1, price:0 }]);
  const removeItem = (i: number) => setOrderItems(prev => prev.filter((_,idx) => idx!==i));
  const updateItem = (i: number, k: string, v: any) => setOrderItems(prev => prev.map((item,idx) => idx===i ? {...item,[k]:v} : item));

  const totalOrder = orderItems.reduce((s,i) => s + (Number(i.price)||0) * (Number(i.quantity)||1), 0);

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderForm.supplier_id) return toast.error('მომწოდებელი სავალდებულოა');
    if (orderItems.every(i => !i.title)) return toast.error('მინიმუმ ერთი წიგნი');
    setSaving(true);
    try {
      await api.post('/acquisitions/orders', { ...orderForm, items: orderItems.filter(i => i.title) });
      toast.success('შეკვეთა შეიქმნა!');
      qc.invalidateQueries({ queryKey: ['orders'] });
      setShowNewOrder(false);
      setOrderItems([{ title:'', author:'', isbn:'', quantity:1, price:0 }]);
      setOrderForm({ supplier_id:'', budget_id:'', order_date:'', expected_date:'', notes:'' });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  const handleStatus = async (id: number, status: string, extra?: any) => {
    const labels: any = { ordered:'შეკვეთილი', received:'მიღებული', cancelled:'გაუქმებული' };
    if (status === 'received') {
      const order = orders.find((o: any) => o.id === id);
      setReceiveOrder(order);
      return;
    }
    if (!window.confirm(`სტატუსი შეიცვლება: "${labels[status]}". გაგრძელება?`)) return;
    try {
      await api.put(`/acquisitions/orders/${id}/status`, { status, ...extra });
      toast.success(status==='received' ? 'წიგნები კატალოგში დაემატა!' : 'სტატუსი განახლდა');
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      setSelectedOrder(null);
    } catch { toast.error('შეცდომა'); }
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>შესყიდვები</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>წიგნების შეძენა, ბიუჯეტი, მომწოდებლები</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {tab==='orders' && <button onClick={() => setShowNewOrder(true)} style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}><Plus size={15}/> ახალი შეკვეთა</button>}
          {tab==='suppliers' && <button onClick={() => setShowNewSupplier(true)} style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}><Plus size={15}/> მომწოდებელი</button>}
          {tab==='budgets' && <button onClick={() => setShowNewBudget(true)} style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}><Plus size={15}/> ბიუჯეტი</button>}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {budgets.slice(0,3).map((b: any) => {
          const pct = Math.min(100, Math.round((b.spent/b.amount)*100));
          return (
            <div key={b.id} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,padding:16}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:12,fontWeight:500,color:'var(--text-secondary)'}}>{b.name}</span>
                <span style={{fontSize:11,color:'var(--text-faint)'}}>{b.year}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:11,color:'var(--text-faint)'}}>დახარჯული</span>
                <span style={{fontSize:13,fontWeight:600,color:'var(--danger)'}}>₾{Number(b.spent).toFixed(2)}</span>
              </div>
              <div style={{height:4,background:'var(--border)',borderRadius:2,marginBottom:6}}>
                <div style={{height:'100%',width:`${pct}%`,background:pct>80?'#ef4444':'#3b82f6',borderRadius:2,transition:'width 0.3s'}} />
              </div>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span style={{fontSize:11,color:'var(--text-faint)'}}>{pct}% გამოყენებული</span>
                <span style={{fontSize:12,fontWeight:600,color:'var(--success)'}}>₾{(b.amount-b.spent).toFixed(2)} დარჩა</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:'1px solid #1e2d45'}}>
        {[{key:'orders',label:'შეკვეთები'},{key:'budgets',label:'ბიუჯეტი'},{key:'suppliers',label:'მომწოდებლები'}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            style={{padding:'8px 16px',background:'none',border:'none',borderBottom:tab===t.key?'2px solid #60a5fa':'2px solid transparent',marginBottom:-1,fontSize:13,color:tab===t.key?'var(--accent)':'var(--text-faint)',cursor:'pointer',fontWeight:tab===t.key?500:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==='orders' && (
        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {['#','მომწოდებელი','ბიუჯეტი','წიგნები','ჯამი','სტატუსი',''].map(h => (
                  <th key={h} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'var(--text-faint)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.length===0 && <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'var(--text-dimmed)'}}>შეკვეთა არ არის</td></tr>}
              {orders.map((o: any) => (
                <tr key={o.id} style={{borderBottom:'1px solid #0f1826',cursor:'pointer'}}
                  onClick={() => setSelectedOrder(o)}
                  onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                  <td style={{padding:'10px 16px',color:'var(--text-dimmed)',fontSize:11}}>#{o.id}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-secondary)',fontWeight:500}}>{o.supplier_name||'—'}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-muted)',fontSize:11}}>{o.budget_name||'—'}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{o.item_count}</td>
                  <td style={{padding:'10px 16px',color:'var(--warning)',fontWeight:500}}>₾{Number(o.total).toFixed(2)}</td>
                  <td style={{padding:'10px 16px'}}>
                    <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:500,background:STATUS_COLORS[o.status]?.bg,color:STATUS_COLORS[o.status]?.color}}>
                      {STATUS_COLORS[o.status]?.label}
                    </span>
                  </td>
                  <td style={{padding:'10px 16px'}}>
                    <div style={{display:'flex',gap:6}} onClick={e => e.stopPropagation()}>
                      {o.status==='draft' && <button onClick={() => handleStatus(o.id,'ordered')} style={{padding:'3px 8px',background:'var(--accent-bg)',border:'1px solid #1e3a6e',borderRadius:6,fontSize:11,color:'var(--accent)',cursor:'pointer'}}>შეკვეთა</button>}
                      {o.status==='ordered' && <button onClick={() => handleStatus(o.id,'received')} style={{padding:'3px 8px',background:'var(--success-bg)',border:'1px solid #0a4a2a',borderRadius:6,fontSize:11,color:'var(--success)',cursor:'pointer'}}>მიღება ✓</button>}
                      {(o.status==='draft'||o.status==='ordered') && <button onClick={() => handleStatus(o.id,'cancelled')} style={{padding:'3px 8px',background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:6,fontSize:11,color:'var(--danger)',cursor:'pointer'}}>გაუქმება</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab==='budgets' && (
        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {['სახელი','წელი','ბიუჯეტი','დახარჯული','დარჩენილი','%'].map(h => (
                  <th key={h} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'var(--text-faint)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {budgets.map((b: any) => {
                const pct = Math.min(100, Math.round((b.spent/b.amount)*100));
                return (
                  <tr key={b.id} style={{borderBottom:'1px solid #0f1826'}}>
                    <td style={{padding:'10px 16px',color:'var(--text-secondary)',fontWeight:500}}>{b.name}</td>
                    <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{b.year}</td>
                    <td style={{padding:'10px 16px',color:'var(--text-primary)',fontWeight:500}}>₾{Number(b.amount).toFixed(2)}</td>
                    <td style={{padding:'10px 16px',color:'var(--danger)'}}>₾{Number(b.spent).toFixed(2)}</td>
                    <td style={{padding:'10px 16px',color:'var(--success)',fontWeight:500}}>₾{(b.amount-b.spent).toFixed(2)}</td>
                    <td style={{padding:'10px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{width:60,height:4,background:'var(--border)',borderRadius:2}}>
                          <div style={{height:'100%',width:`${pct}%`,background:pct>80?'#ef4444':'#3b82f6',borderRadius:2}} />
                        </div>
                        <span style={{fontSize:11,color:'var(--text-muted)'}}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab==='suppliers' && (
        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {['სახელი','საკონტაქტო','ელ-ფოსტა','ტელეფონი'].map(h => (
                  <th key={h} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'var(--text-faint)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s: any) => (
                <tr key={s.id} style={{borderBottom:'1px solid #0f1826'}}>
                  <td style={{padding:'10px 16px',color:'var(--text-secondary)',fontWeight:500}}>{s.name}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{s.contact||'—'}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{s.email||'—'}</td>
                  <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{s.phone||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!receiveOrder} onClose={() => setReceiveOrder(null)} title="წიგნების მიღება" width={560}>
        {receiveOrder && (
          <form onSubmit={async e => {
            e.preventDefault();
            if (!receiveForm.account_code) return toast.error('საბუღ. ანგარიში სავალდებულოა');
            if (!receiveForm.acquisition_price) return toast.error('ფასი სავალდებულოა');
            setSaving(true);
            try {
              await api.put(`/acquisitions/orders/${receiveOrder.id}/status`, {
                status: 'received',
                account_code: receiveForm.account_code,
                acquisition_price: parseFloat(receiveForm.acquisition_price),
                receive_date: receiveForm.receive_date
              });
              toast.success('წიგნები მიღებულია და კატალოგში დაემატა!');
              qc.invalidateQueries({ queryKey: ['orders'] });
              qc.invalidateQueries({ queryKey: ['stats'] });
              setReceiveOrder(null);
            } catch { toast.error('შეცდომა'); }
            finally { setSaving(false); }
          }}>
            <div style={{background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:10,padding:14,marginBottom:14}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <div style={{padding:'8px 10px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8}}>
                  <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:2}}>მომწოდებელი</div>
                  <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500}}>{receiveOrder.supplier_name||'—'}</div>
                </div>
                <div style={{padding:'8px 10px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8}}>
                  <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:2}}>წიგნების რაოდენობა</div>
                  <div style={{fontSize:16,color:'var(--accent)',fontWeight:700}}>{receiveOrder.item_count} სახელწ.</div>
                </div>
              </div>
              <div style={{padding:'8px 10px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8}}>
                <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:2}}>შეკვეთის ჯამი</div>
                <div style={{fontSize:16,color:'var(--warning)',fontWeight:700}}>₾{Number(receiveOrder.total).toFixed(2)}</div>
              </div>
            </div>

            <FormField label="საბუღ. ანგარიში" required>
              <Select value={receiveForm.account_code} onChange={e => setReceiveForm(f => ({...f,account_code:e.target.value}))}>
                <option value="">— ანგარიში —</option>
                {((suppliersData as any)?.data?.accounts||[]).map((a: any) => (
                  <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </Select>
            </FormField>

            <FormRow>
              <FormField label="ერთეულის ფასი (₾)" required>
                <Input type="number" min="0" step="0.01" value={receiveForm.acquisition_price}
                  onChange={e => setReceiveForm(f => ({...f,acquisition_price:e.target.value}))}
                  placeholder="0.00" />
              </FormField>
              <FormField label="მიღების თარიღი" required>
                <Input type="date" value={receiveForm.receive_date}
                  onChange={e => setReceiveForm(f => ({...f,receive_date:e.target.value}))} />
              </FormField>
            </FormRow>

            {receiveForm.acquisition_price && receiveOrder.item_count && (
              <div style={{background:'var(--success-bg)',border:'1px solid #0a4a2a',borderRadius:8,padding:12,marginBottom:14}}>
                <div style={{fontSize:11,color:'var(--success)',marginBottom:4}}>ჯამური საინვენტარო ღირებულება:</div>
                <div style={{fontSize:20,fontWeight:700,color:'var(--success)'}}>
                  ₾{(parseFloat(receiveForm.acquisition_price||'0') * receiveOrder.item_count).toFixed(2)}
                </div>
                <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>
                  ₾{receiveForm.acquisition_price} × {receiveOrder.item_count} ასლი
                </div>
              </div>
            )}

            <SubmitBtn loading={saving} label="✓ მიღება და კატალოგში დამატება" />
          </form>
        )}
      </Modal>

      <Modal open={showNewOrder} onClose={() => setShowNewOrder(false)} title="ახალი შეკვეთა" width={620}>
        <form onSubmit={handleCreateOrder}>
          <FormRow>
            <FormField label="მომწოდებელი" required>
              <Select value={orderForm.supplier_id} onChange={e => setOrderForm(f => ({...f,supplier_id:e.target.value}))}>
                <option value="">— აირჩიე —</option>
                {suppliers.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </FormField>
            <FormField label="ბიუჯეტი">
              <Select value={orderForm.budget_id} onChange={e => setOrderForm(f => ({...f,budget_id:e.target.value}))}>
                <option value="">— ბიუჯეტი —</option>
                {budgets.map((b: any) => <option key={b.id} value={b.id}>{b.name} (₾{(b.amount-b.spent).toFixed(0)} დარჩა)</option>)}
              </Select>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="შეკვეთის თარიღი">
              <Input type="date" value={orderForm.order_date} onChange={e => setOrderForm(f => ({...f,order_date:e.target.value}))} />
            </FormField>
            <FormField label="მოლოდინის თარიღი">
              <Input type="date" value={orderForm.expected_date} onChange={e => setOrderForm(f => ({...f,expected_date:e.target.value}))} />
            </FormField>
          </FormRow>

          <div style={{marginBottom:14}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <label style={{fontSize:12,color:'var(--text-muted)'}}>წიგნები</label>
              <button type="button" onClick={addItem} style={{padding:'3px 10px',background:'var(--accent-bg)',border:'1px solid #1e3a6e',borderRadius:6,fontSize:11,color:'var(--accent)',cursor:'pointer'}}>+ დამატება</button>
            </div>
            {orderItems.map((item, i) => (
              <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 0.7fr 0.7fr auto',gap:6,marginBottom:6,alignItems:'center'}}>
                <Input value={item.title} onChange={e => updateItem(i,'title',e.target.value)} placeholder="სათაური *" />
                <Input value={item.author} onChange={e => updateItem(i,'author',e.target.value)} placeholder="ავტორი" />
                <Input value={item.isbn} onChange={e => updateItem(i,'isbn',e.target.value)} placeholder="ISBN" />
                <Input type="number" value={item.quantity} onChange={e => updateItem(i,'quantity',e.target.value)} min="1" placeholder="რაოდ." />
                <Input type="number" value={item.price} onChange={e => updateItem(i,'price',e.target.value)} min="0" step="0.01" placeholder="ფასი" />
                <button type="button" onClick={() => removeItem(i)} style={{background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:6,padding:'7px 8px',color:'var(--danger)',cursor:'pointer',fontSize:13}}>×</button>
              </div>
            ))}
            <div style={{textAlign:'right',fontSize:13,color:'var(--warning)',fontWeight:600,marginTop:8}}>
              ჯამი: ₾{totalOrder.toFixed(2)}
            </div>
          </div>

          <FormField label="შენიშვნა">
            <Textarea value={orderForm.notes} onChange={e => setOrderForm(f => ({...f,notes:e.target.value}))} placeholder="დამატებითი ინფო..." />
          </FormField>
          <SubmitBtn loading={saving} label="შეკვეთის შექმნა" />
        </form>
      </Modal>

      <Modal open={showNewSupplier} onClose={() => setShowNewSupplier(false)} title="მომწოდებლის დამატება" width={480}>
        <form onSubmit={async e => { e.preventDefault(); try { await api.post('/acquisitions/suppliers', supplierForm); toast.success('დაემატა!'); qc.invalidateQueries({queryKey:['suppliers']}); setShowNewSupplier(false); } catch { toast.error('შეცდომა'); }}}>
          <FormField label="სახელი" required><Input value={supplierForm.name} onChange={e => setSupplierForm(f => ({...f,name:e.target.value}))} placeholder="კომპანიის სახელი" /></FormField>
          <FormRow>
            <FormField label="საკონტაქტო"><Input value={supplierForm.contact} onChange={e => setSupplierForm(f => ({...f,contact:e.target.value}))} placeholder="სახელი გვარი" /></FormField>
            <FormField label="ტელეფონი"><Input value={supplierForm.phone} onChange={e => setSupplierForm(f => ({...f,phone:e.target.value}))} placeholder="+995..." /></FormField>
          </FormRow>
          <FormField label="ელ-ფოსტა"><Input type="email" value={supplierForm.email} onChange={e => setSupplierForm(f => ({...f,email:e.target.value}))} placeholder="info@..." /></FormField>
          <FormField label="მისამართი"><Input value={supplierForm.address} onChange={e => setSupplierForm(f => ({...f,address:e.target.value}))} placeholder="მისამართი" /></FormField>
          <SubmitBtn loading={false} label="დამატება" />
        </form>
      </Modal>

      <Modal open={showNewBudget} onClose={() => setShowNewBudget(false)} title="ბიუჯეტის დამატება" width={420}>
        <form onSubmit={async e => { e.preventDefault(); try { await api.post('/acquisitions/budgets', budgetForm); toast.success('დაემატა!'); qc.invalidateQueries({queryKey:['budgets']}); setShowNewBudget(false); } catch { toast.error('შეცდომა'); }}}>
          <FormField label="სახელი" required><Input value={budgetForm.name} onChange={e => setBudgetForm(f => ({...f,name:e.target.value}))} placeholder="ბიუჯეტის სახელი" /></FormField>
          <FormRow>
            <FormField label="თანხა (₾)" required><Input type="number" value={budgetForm.amount} onChange={e => setBudgetForm(f => ({...f,amount:e.target.value}))} placeholder="5000" /></FormField>
            <FormField label="წელი"><Input type="number" value={budgetForm.year} onChange={e => setBudgetForm(f => ({...f,year:e.target.value}))} /></FormField>
          </FormRow>
          <SubmitBtn loading={false} label="შექმნა" />
        </form>
      </Modal>

      <Modal open={!!selectedOrder} onClose={() => setSelectedOrder(null)} title={`შეკვეთა #${selectedOrder?.id}`} width={560}>
        {detail && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
              {[{l:'მომწოდებელი',v:detail.supplier_name},{l:'ბიუჯეტი',v:detail.budget_name},{l:'შეკვეთის თარიღი',v:detail.order_date?new Date(detail.order_date).toLocaleDateString():'-'},{l:'მოლოდინის თარიღი',v:detail.expected_date?new Date(detail.expected_date).toLocaleDateString():'-'}].map(r => (
                <div key={r.l} style={{background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 12px'}}>
                  <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:3}}>{r.l}</div>
                  <div style={{fontSize:13,color:'var(--text-secondary)'}}>{r.v||'—'}</div>
                </div>
              ))}
            </div>
            <div style={{marginBottom:12}}>
              {(detail.items||[]).map((item: any) => (
                <div key={item.id} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #0f1826',fontSize:13}}>
                  <div>
                    <div style={{color:'var(--text-secondary)',fontWeight:500}}>{item.title}</div>
                    <div style={{fontSize:11,color:'var(--text-faint)'}}>{item.author||''} {item.isbn?`· ${item.isbn}`:''}</div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                    <div style={{color:'var(--warning)',fontWeight:500}}>₾{Number(item.price).toFixed(2)} × {item.quantity}</div>
                    <div style={{fontSize:11,color:'var(--text-faint)'}}>= ₾{(item.price*item.quantity).toFixed(2)}</div>
                  </div>
                </div>
              ))}
              <div style={{textAlign:'right',padding:'8px 0',fontSize:14,fontWeight:700,color:'var(--warning)'}}>
                ჯამი: ₾{Number(detail.total).toFixed(2)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
